import { useSeo } from "../lib/useSeo";
import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Check, Wifi, Leaf, Users, Ticket } from "lucide-react";
import { ASSETS } from "../lib/constants";
import { Reveal } from "../components/Reveal";
import { Label } from "../components/bits";
import { subscribe } from "../lib/api";

const AGE_KEY = "hgdc_age_verified";

const expects = [
  { icon: Wifi, text: "A real workspace: Wi-Fi, coffee, and a table for as long as you need it" },
  { icon: Leaf, text: "A full menu of flower, edibles, and vapes, self-certify on-site in minutes" },
  { icon: Users, text: "Live events, tastings, and a room full of regulars" },
];

const VisitAgeGate = ({ onConfirm }) => (
  <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-ink px-6" data-testid="visit-age-gate">
    <div className="absolute inset-0 opacity-20">
      <img src={ASSETS.exteriorWide} alt="" className="h-full w-full object-cover" />
    </div>
    <div className="absolute inset-0 bg-ink/85" />
    <motion.div
      className="relative z-10 w-full max-w-lg border border-white/10 bg-ink-2/80 p-10 text-center backdrop-blur-xl md:p-14"
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      <img src={ASSETS.logo} alt="Higher Ground DC" className="mx-auto mb-8 h-20 w-auto object-contain" />
      <h2 className="font-display text-2xl font-light uppercase tracking-[0.12em] text-white md:text-3xl">
        Are you 21 or older?
      </h2>
      <p className="mx-auto mt-5 max-w-sm text-sm leading-relaxed text-white/60">
        You must be 21+ to enter Higher Ground DC.
      </p>
      <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:justify-center">
        <button
          onClick={onConfirm}
          data-testid="visit-age-confirm"
          className="bg-gold px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-champagne"
        >
          Yes, I'm 21+
        </button>
        <a
          href="https://www.google.com"
          data-testid="visit-age-deny"
          className="border border-white/15 px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:border-white/40 hover:text-white"
        >
          No
        </a>
      </div>
    </motion.div>
  </div>
);

export default function Visit() {
  useSeo("/visit");
  const [verified, setVerified] = useState(() => !!sessionStorage.getItem(AGE_KEY));
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const confirmAge = () => {
    sessionStorage.setItem(AGE_KEY, "1");
    window.dispatchEvent(new Event("hgdc-age-verified"));
    setVerified(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribe({ email, interest: "First Visit Pass" });
      setDone(true);
    } catch {
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  if (!verified) return <VisitAgeGate onConfirm={confirmAge} />;

  return (
    <div data-testid="visit-page">
      {/* HERO */}
      <section className="relative flex min-h-[72vh] items-end overflow-hidden pb-16 pt-40 md:min-h-[80vh]" data-testid="visit-hero">
        <div className="absolute inset-0">
          <img src={ASSETS.groupPatio} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/40" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 lg:px-12">
          <Reveal><Label>First Visit Pass</Label></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-light uppercase leading-[1.05] tracking-[0.08em] text-white sm:text-5xl lg:text-6xl">
              DC's Creative Workspace
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 lg:text-lg">
              Laptops, coffee, joints, deadlines. Higher Ground is DC's only legal cannabis lounge, and the
              only place in the city where all four belong at the same table. Work through the afternoon,
              stay for the events.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHAT TO EXPECT + FORM */}
      <section className="bg-ink py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] items-start gap-16 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <Reveal><Label>What to Expect</Label></Reveal>
            <ul className="mt-8 space-y-6">
              {expects.map((x, i) => (
                <Reveal key={i} delay={0.08 * i}>
                  <li className="flex items-start gap-4 text-white/75">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-gold/40 text-gold">
                      <x.icon className="h-5 w-5" />
                    </span>
                    <span className="pt-2 leading-relaxed">{x.text}</span>
                  </li>
                </Reveal>
              ))}
            </ul>

            <Reveal delay={0.2}>
              <div className="mt-10 border-l-2 border-gold bg-gold/10 p-6">
                <p className="text-xs uppercase tracking-[0.22em] text-gold">First Visit Pass</p>
                <p className="mt-3 font-display text-2xl font-light uppercase tracking-[0.05em] text-white">
                  Free joint with your first purchase of $30+
                </p>
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  Adults 21+, self-certify as a DC medical patient in minutes, right at the counter.
                </p>
              </div>
            </Reveal>
          </div>

          {/* FORM */}
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-ink-2/90 p-8 backdrop-blur-xl md:p-10" data-testid="visit-form">
              {done ? (
                <div className="flex flex-col items-center py-10 text-center" data-testid="visit-success">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/50 bg-gold/10">
                    <Ticket className="h-8 w-8 text-gold" />
                  </div>
                  <h2 className="mt-6 font-display text-2xl font-light uppercase tracking-[0.1em] text-white">
                    Pass on its way
                  </h2>
                  <p className="mt-4 max-w-xs leading-relaxed text-white/60">
                    Your First Visit Pass is on its way, check your email. Show it at the counter on your
                    first visit for your free joint.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <h2 className="font-display text-2xl font-light uppercase tracking-[0.1em] text-white">
                    Get My First Visit Pass
                  </h2>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    data-testid="visit-email"
                    className="mt-8 w-full border border-white/15 bg-ink px-4 py-4 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-gold"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="visit-submit"
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get My First Visit Pass"}
                  </button>
                  <p className="mt-6 text-xs leading-relaxed text-white/40">
                    Must be 21+. DC medical self-certification required for purchase. Free joint rings up at
                    $0.01 with a qualifying $30+ pre-tax purchase. One First Visit Pass per new customer.
                    Terms apply.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
